import { useAppSelector } from 'appState/store'
import { Input } from 'components/Forms'
import { apiPaths, RelativeRootEnum } from 'configs/apiConfigs'
import { CSCreateQuery, CSRestQuery, Filter } from 'features/claims/claimTypes'
import { useEffect, useState } from 'react'
import { FaPaperPlane } from 'react-icons/fa'
import { Button, ObModal } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { httpTenantsService } from 'utils/http'
import { emailRegex } from 'utils/misc'
import { OaiUserCreate } from '../types/userTypes'
import CustomCheckbox from '../utils/CustomCheckbox'
import { extraPermissions, getPermissionList } from '../utils/UserConstants'

interface IProps {
  show: boolean
  hide: () => void
  title: string | JSX.Element
  createUser: (value: OaiUserCreate) => void
}

export const CSCreateUserModal = ({ show, hide, title, createUser }: IProps) => {
  const [step, setStep] = useState<number>(1)

  //get current user details

  const [email, setEmail] = useState<string>('')
  const [firstName, setFirstName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')

  const [emailError, setEmailError] = useState<string>('')
  const [isCheckingEmail, setIsCheckingEmail] = useState<boolean>(false)

  const [customerList, setCustomerList] = useState<any[]>([])
  const [permissions, setPermissions] = useState<string[]>([])
  const [practices, setPractices] = useState<string[]>([])

  const permissionsList = getPermissionList()

  const currentCustomer = useAppSelector((state) => state.auth.currentCustomer)
  const currentUser = useAppSelector((state) => state.auth.user)

  const isOutboundUser = currentUser?.email?.endsWith('@outbound.ai') && email.endsWith('@outbound.ai')

  const outboundUserPermissions = [...permissionsList, ...extraPermissions()]

  useEffect(() => {
    if (!show || !currentCustomer) {
      return
    }

    getCustomerList(currentCustomer)
  }, [show, currentCustomer])

  function getCustomerList(customerId: string) {
    const oaiCustomerQuery = new CSRestQuery(99998, 0)
    oaiCustomerQuery.addFilter(new Filter('customerid', '=', customerId))
    const manageQuery = new CSCreateQuery(oaiCustomerQuery)
    const url = apiPaths.csSearch(RelativeRootEnum.cppuCustomers, manageQuery)

    httpTenantsService
      .get(url)
      .then((res) => {
        const customers = res.data.data
        if (customers && customers?.length > 0) {
          setCustomerList(customers[0].practices.sort((a: any, b: any) => a.name.localeCompare(b.name)))
        } else {
          setCustomerList([])
        }
      })
      .catch((error) => {
        console.error('Error fetching customer list:', error)
        setCustomerList([])
      })
  }

  const totalItems = customerList.length
  const mid = Math.ceil(totalItems / 2)

  const firstColumn = customerList.slice(0, mid)
  const secondColumn = customerList.slice(mid)

  const handleCheckboxChange = (value: string, setter: (values: string[]) => void, currentValues: string[]) => {
    if (currentValues.includes(value)) {
      setter(currentValues.filter((item) => item !== value))
    } else {
      setter([...currentValues, value])
    }
  }

  const isPage1Valid = emailRegex.test(email) && firstName.trim() !== '' && lastName.trim() !== ''

  const validateEmailWithAPI = async (): Promise<boolean> => {
    setIsCheckingEmail(true)
    setEmailError('')
    try {
      const oaiCustomerQuery = new CSRestQuery(0, 0)
      oaiCustomerQuery.addFilter(new Filter('email', 'like', email))
      const manageQuery = new CSCreateQuery(oaiCustomerQuery)
      const url = apiPaths.csSearch(RelativeRootEnum.cppuOaiUsers, manageQuery)
      const response = await httpTenantsService.get(url)
      const isValid = response.data?.count === 0

      if (!isValid) {
        setEmailError('This email is already in use or invalid.')
      }

      return isValid
    } catch (error) {
      setEmailError('Something went wrong while validating email.')
      return false
    } finally {
      setIsCheckingEmail(false)
    }
  }

  const resetForm = () => {
    setEmail('')
    setFirstName('')
    setLastName('')
    setPermissions([])
    setPractices([])
    setStep(1)
    setEmailError('')
  }

  const handleClose = () => {
    resetForm()
    hide()
  }

  const handleSubmit = () => {
    const newUser: OaiUserCreate = {
      email,
      username: email,
      familyName: lastName,
      givenName: firstName,
      permissionIds: permissions,
      practiceIds: practices,
    }
    createUser(newUser)
    handleClose()
  }

  return (
    <ObModal show={show} close={handleClose} title={title} customSize="medium" size="lg" height={'auto'}>
      {step === 1 ? (
        <Div>
          <Input type="email" label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          {emailError && <ErrorText>{emailError}</ErrorText>}

          <Input label="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          <Input label="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </Div>
      ) : (
        <Div>
          <PractiseGrid>
            <div>
              {firstName} {lastName}
            </div>
            <div>{email}</div>
          </PractiseGrid>
          <SectionTitle>Permissions</SectionTitle>
          <PermissionsGrid>
            {isOutboundUser
              ? outboundUserPermissions.map((perm) => (
                <label key={perm.id}>
                  <CustomCheckbox
                    checked={permissions.includes(perm.id)}
                    onChange={() => handleCheckboxChange(perm.id, setPermissions, permissions)}
                  />
                  {perm.name}
                </label>
              ))
              : permissionsList.map((perm) => (
                <label key={perm.id}>
                  <CustomCheckbox
                    checked={permissions.includes(perm.id)}
                    onChange={() => handleCheckboxChange(perm.id, setPermissions, permissions)}
                  />
                  {perm.name}
                </label>
              ))}
          </PermissionsGrid>
          <SectionTitle>Access to Practices</SectionTitle>
          <PractiseGrid>
            <div>
              {firstColumn.map((practice) => (
                <label key={practice.practiceId}>
                  <CustomCheckbox
                    checked={practices.includes(practice.practiceId)}
                    onChange={() => handleCheckboxChange(practice.practiceId, setPractices, practices)}
                  />
                  {practice.name}
                </label>
              ))}
            </div>

            <div>
              {secondColumn.map((practice) => (
                <label key={practice.practiceId}>
                  <CustomCheckbox
                    checked={practices.includes(practice.practiceId)}
                    onChange={() => handleCheckboxChange(practice.practiceId, setPractices, practices)}
                  />
                  {practice.name}
                </label>
              ))}
            </div>
          </PractiseGrid>
        </Div>
      )}

      <FooterDiv>
        {step === 1 ? (
          <>
            <Button radius="rounded" className="footer-btn" variant="primary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              radius="rounded"
              className="footer-btn"
              variant="primary-outline"
              disabled={!isPage1Valid || isCheckingEmail}
              onClick={async () => {
                const isValid = await validateEmailWithAPI()
                if (isValid) setStep(2)
              }}
            >
              {isCheckingEmail ? 'Checking...' : 'Next'}
            </Button>
          </>
        ) : (
          <>
            <Button radius="rounded" className="footer-btn" variant="primary-outline" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button radius="rounded" className="footer-btn" variant="primary" onClick={handleSubmit}>
              <FaPaperPlane /> Send Invite
            </Button>
          </>
        )}
      </FooterDiv>
    </ObModal>
  )
}

const Div = styled.div`
  display: flex;
  padding: 1rem !important;
  flex-direction: column;
  gap: 1rem;
`

const SectionTitle = styled.h4`
  margin: 1rem 0 0.5rem;
  font-weight: bold;
`

const PermissionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;

  label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`

const PractiseGrid = styled.div`
  display: flex;
  gap: 0.75rem;
  max-height: 200px;
  overflow-y: auto;
  padding-right: 0.5rem;

  /* each column */
  > div {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`

const FooterDiv = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1rem;
`

const ErrorText = styled.p`
  color: red;
  margin-top: -0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
`
